<template>
  <md-dialog :md-active.sync="active" class="assign-area-modal" @md-closed="onClosed">
    <md-dialog-title>
      Assign Biotime Area to {{ labourerName }}
    </md-dialog-title>

    <md-dialog-content>
      <div class="modal-content">
        <!-- Search + filter -->
        <div class="modal-filters">
          <md-field>
            <label>Search areas</label>
            <md-input
                v-model="search"
                placeholder="Search by code or name..."
                @input="onSearch"
            />
          </md-field>

          <md-field>
            <label>Station</label>
            <md-select v-model="stationFilter" @md-selected="onStationFilter">
              <md-option :value="null">All Stations</md-option>
              <md-option :value="'unassigned'">Unassigned</md-option>
              <md-option
                  v-for="s in stations"
                  :key="s.id"
                  :value="s.id"
              >
                {{ s.name }}
              </md-option>
            </md-select>
          </md-field>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="modal-loading">
          <md-progress-spinner md-mode="indeterminate" :md-diameter="36"></md-progress-spinner>
          <span>Loading areas...</span>
        </div>

        <!-- Area list -->
        <div v-else class="area-list">
          <div
              v-for="area in filteredAreas"
              :key="area.id"
              class="area-row"
              :class="{ assigned: isAssigned(area.id) }"
              @click="toggleArea(area)"
          >
            <md-checkbox
                :value="!isAssigned(area.id)"
                @change="toggleArea(area)"
                class="area-checkbox"
            />
            <div class="area-info">
              <div class="area-title">
                <strong>{{ area.area_name }}</strong>
                <chip v-if="area.station_name" type="info" icon="business" size="small">
                  {{ area.station_name }}
                </chip>
                <chip v-else type="warning" icon="help_outline" size="small">
                  Unassigned
                </chip>
              </div>
              <div class="area-sub">
                <code>{{ area.area_code }}</code>
                <span v-if="area.parent_area">· {{ area.parent_area }}</span>
              </div>
            </div>
            <chip
                v-if="isAssigned(area.id)"
                type="success"
                icon="check_circle"
                size="small"
            >
              Assigned
            </chip>
          </div>

          <div v-if="filteredAreas.length === 0" class="empty-areas">
            <md-icon>inbox</md-icon>
            <p>No areas found</p>
          </div>
        </div>

        <!-- Selection summary -->
        <div class="selection-summary" v-if="pendingSelection.length">
          <md-icon>check_circle</md-icon>
          <span>
            {{ pendingSelection.length }} area{{ pendingSelection.length === 1 ? '' : 's' }}
            selected (pending save)
          </span>
        </div>

        <!-- Error -->
        <div v-if="errorMessage" class="error-banner">
          <md-icon>error</md-icon>
          <span>{{ errorMessage }}</span>
        </div>
      </div>
    </md-dialog-content>

    <md-dialog-actions>
      <md-button
          class="md-primary"
          @click="save"
          :disabled="isSaving || !hasPendingChanges"
      >
        {{ isSaving ? 'Saving...' : 'Save Assignments' }}
      </md-button>
      <md-button class="md-danger" @click="close">Cancel</md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
import Chip from '@/components/Chip.vue';
import _ from 'lodash';

export default {
  name: 'AssignBiotimeAreaModal',
  components: { Chip },
  props: {
    value: { type: Boolean, default: false },
    labourerId: { type: [Number, String], required: true },
    labourerName: { type: String, default: 'Labourer' },
    /** Existing assigned area IDs (from the parent) */
    assignedAreaIds: {
      type: Array,
      default: function () { return []; }
    }
  },
  data() {
    return {
      search: '',
      stationFilter: null,
      stations: [],
      areas: [],
      isLoading: false,
      isSaving: false,
      errorMessage: null,
      /** Areas we've toggled since opening */
      pendingSelection: [],
      /** Snapshot of what was originally assigned */
      originalAssignedIds: []
    };
  },
  computed: {
    active: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); }
    },
    filteredAreas: function () {
      var self = this;
      var list = this.areas;

      if (this.search) {
        var q = this.search.toLowerCase();
        list = list.filter(function (a) {
          return (a.area_code || '').toLowerCase().indexOf(q) !== -1
              || (a.area_name || '').toLowerCase().indexOf(q) !== -1
              || (a.parent_area || '').toLowerCase().indexOf(q) !== -1;
        });
      }

      if (this.stationFilter === 'unassigned') {
        list = list.filter(function (a) { return !a.station_id; });
      } else if (this.stationFilter) {
        list = list.filter(function (a) { return a.station_id === self.stationFilter; });
      }

      return list;
    },
    hasPendingChanges: function () {
      if (this.pendingSelection.length !== this.originalAssignedIds.length) return true;
      var set = {};
      this.originalAssignedIds.forEach(function (id) { set[id] = true; });
      return this.pendingSelection.some(function (id) { return !set[id]; });
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) this.init();
      }
    }
  },
  methods: {
    async init() {
      this.errorMessage = null;
      this.pendingSelection = [].concat(this.assignedAreaIds || []);
      this.originalAssignedIds = [].concat(this.assignedAreaIds || []);
      this.search = '';
      this.stationFilter = null;
      await this.loadStations();
      await this.loadAreas();
    },

    async loadStations() {
      try {
        await this.$store.dispatch('stations/list', { page: { size: 1000 } });
        this.stations = this.$store.getters['stations/list'] || [];
      } catch (e) {
        this.stations = [];
      }
    },

    async loadAreas() {
      this.isLoading = true;
      try {
        await this.$store.dispatch('biotimeAreas/list', {
          page: { size: 100 },
          filter: {}
        });
        this.areas = this.$store.getters['biotimeAreas/list'] || [];
      } catch (e) {
        this.errorMessage = 'Failed to load areas';
        this.areas = [];
      } finally {
        this.isLoading = false;
      }
    },

    onSearch: _.debounce(function () {
      // Client-side filter — no fetch needed
    }, 150),

    onStationFilter: function () {
      // Client-side filter
    },

    isAssigned: function (areaId) {
      return this.pendingSelection.indexOf(areaId) !== -1;
    },

    toggleArea: function (area) {
      var idx = this.pendingSelection.indexOf(area.id);
      if (idx >= 0) {
        this.pendingSelection.splice(idx, 1);
      } else {
        this.pendingSelection.push(area.id);
      }
    },

    async save() {
      this.isSaving = true;
      this.errorMessage = null;

      try {
        await this.$store.dispatch('labourers/syncBiotimeAreas', {
          labourer_id: this.labourerId,
          biotime_area_ids: this.pendingSelection
        });

        this.$store.dispatch('alerts/success', 'Biotime area assignments updated');
        this.$emit('success');
        this.close();
      } catch (error) {
        console.error('Failed to save assignments', error);
        this.errorMessage = 'Failed to save. Please try again.';
        this.$emit('error', error);
      } finally {
        this.isSaving = false;
      }
    },

    close() {
      this.active = false;
    },

    onClosed() {
      this.pendingSelection = [];
      this.originalAssignedIds = [];
      this.search = '';
      this.stationFilter = null;
      this.errorMessage = null;
      this.$emit('closed');
    }
  }
};
</script>

<style lang="scss" scoped>
.assign-area-modal {
  ::v-deep .md-dialog {
    max-width: 640px;
    width: 90%;
  }
  ::v-deep .md-dialog-title {
    padding: 20px 24px 0;
    font-size: 18px;
    font-weight: 600;
  }
  ::v-deep .md-dialog-content {
    padding: 8px 24px 16px;
  }
  ::v-deep .md-dialog-actions {
    padding: 8px 24px 20px;
  }
}

.modal-content {
  min-width: 480px;
  padding-top: 8px;
}

.modal-filters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: #999;
  gap: 12px;
}

.area-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fafbfc;
}

.area-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f5f7ff;
  }

  &.assigned {
    background: #f1f8f2;

    &:hover {
      background: #e8f5e9;
    }
  }
}

.area-checkbox {
  flex-shrink: 0;
}

.area-info {
  flex: 1;
  min-width: 0;
}

.area-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.area-sub {
  font-size: 12px;
  color: #999;

  code {
    background: #f0f0f5;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 11px;
    color: #555;
  }
}

.empty-areas {
  text-align: center;
  padding: 40px 20px;
  color: #999;

  .md-icon {
    font-size: 40px !important;
    width: 40px !important;
    height: 40px !important;
    color: #ddd;
    margin-bottom: 8px;
  }
}

.selection-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 6px;
  font-size: 13px;
  margin-top: 12px;

  .md-icon {
    color: #2e7d32;
  }
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #ffebee;
  color: #c62828;
  border-radius: 6px;
  font-size: 13px;
  margin-top: 12px;

  .md-icon {
    color: #c62828;
  }
}

@media (max-width: 768px) {
  .modal-content {
    min-width: auto;
  }
  .modal-filters {
    grid-template-columns: 1fr;
  }
}
</style>